import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminNav } from "@/components/AdminNav";
import { api } from "@/lib/api";
import { SECTIONS, STAGES, sectionsForStage, isShown } from "@/lib/betaReport/schema";
import { builtInStrings, applyWording, LANGS } from "@/lib/betaReport/i18n";
import { editableKeys, textAt, loadBetaReportWording } from "@/lib/betaReport/wording";
import { Question } from "@/pages/BetaReport";

// Admin → Beta Report Wording (product owner, 2026-11-04). Edit the form's wording in
// English; "Save and translate" makes the Portuguese, Spanish and French versions, which
// you can check and tweak; testers only see it after "Publish". Wording only: the
// questions, order, required and follow-ups don't change here.

const LANG_LABEL = { en: "English", "pt-BR": "Português (Brasil)", "es-419": "Español", "fr-CA": "Français (Canada)" };
const ALL_KEYS = editableKeys();

const optionKeys = (q) => {
  if (q.type === "rating" || q.type === "rating_na") return [1, 2, 3, 4, 5].map((n) => `ratings.${n}`).concat(q.type === "rating_na" ? ["ui.na"] : []);
  if (q.type === "yesno") return ["opt.yes", "opt.no"];
  if (q.type === "choice" || q.type === "multi") return q.options.map((o) => `opt.${o}`);
  if (q.type === "question_number") return ["ui.pickQuestion", "ui.noQuestion"];
  if (q.type === "language") return ["ui.languageAuto"];
  return [];
};

// Where each option text is used, so the editor can say it changes everywhere.
const USES = (() => {
  const uses = {};
  for (const section of SECTIONS) for (const q of section.questions) for (const k of optionKeys(q)) uses[k] = (uses[k] || 0) + 1;
  return uses;
})();

const GENERAL = [
  ["Top of the form", ["ui.eyebrow", "ui.title", "ui.intro", "ui.mode"]],
  ["Steps", STAGES.map((s) => `stages.${s}`)],
  ["Buttons and messages", ALL_KEYS.filter((k) => k.startsWith("ui.") && !["ui.eyebrow", "ui.title", "ui.intro", "ui.mode", "ui.na", "ui.pickQuestion", "ui.noQuestion", "ui.languageAuto"].includes(k))],
];

export default function AdminBetaReportWording() {
  const [server, setServer] = useState(null);
  const [edits, setEdits] = useState({});
  const [lang, setLang] = useState("en");
  const [view, setView] = useState("edit");
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/admin/beta-report/wording").then(({ data }) => setServer(data))
    .catch(() => toast.error("Couldn't load the Beta Report wording.")), []);
  useEffect(() => { load(); }, [load]);

  const draft = useMemo(() => server?.draft || {}, [server]);
  const savedText = (l, key) => draft[l]?.[key] ?? textAt(builtInStrings(l), key) ?? "";
  const value = (l, key) => edits[l]?.[key] ?? savedText(l, key);
  const dirty = Object.values(edits).some((m) => Object.keys(m).length > 0);
  const missing = useMemo(() => new Set((server?.missing || []).map((m) => `${m.lang}|${m.key}`)), [server]);
  const changedKeys = useMemo(() => new Set(Object.keys(draft.en || {}).concat(Object.keys(edits.en || {}))), [draft, edits]);

  const setText = (l, key, text) => setEdits((prev) => {
    const mine = { ...(prev[l] || {}) };
    if (text === savedText(l, key)) delete mine[key];
    else mine[key] = text;
    return { ...prev, [l]: mine };
  });
  const resetKey = (key) => setText("en", key, textAt(builtInStrings("en"), key));

  const save = async () => {
    const changes = Object.entries(edits.en || {}).map(([key, text]) => {
      const original = textAt(builtInStrings("en"), key);
      const back = text.trim() === original;
      return {
        key, text: back ? null : text, before: savedText("en", key),
        before_translations: Object.fromEntries(LANGS.filter((l) => l !== "en").map((l) => [l, savedText(l, key)])),
      };
    });
    const translations = Object.fromEntries(LANGS.filter((l) => l !== "en" && Object.keys(edits[l] || {}).length)
      .map((l) => [l, Object.fromEntries(Object.entries(edits[l]).filter(([k]) => !(edits.en || {})[k]))]));
    if (changes.some((c) => c.text !== null && !c.text.trim())) {
      toast.error("A text can't be empty. Use \"Original\" to go back to the built-in wording.");
      return;
    }
    setBusy("save");
    try {
      const { data } = await api.put("/admin/beta-report/wording/draft", { changes, translations });
      setServer((prev) => ({ ...prev, ...data }));
      setEdits({});
      toast.success(changes.length ? `Saved. ${data.translated} text(s) translated. Check them, then Publish.` : "Saved.");
      if (data.missing?.length) toast.error(`${data.missing.length} translation(s) failed. Type them in, or save again.`);
    } catch {
      toast.error("Couldn't save. Nothing was changed.");
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      await api.post("/admin/beta-report/wording/publish");
      await load();
      loadBetaReportWording({ force: true });
      toast.success("Published. Testers now see the new wording in all four languages.");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail?.code === "missing_translations"
        ? "Some translations are missing (marked in red)." : "Couldn't publish.");
    } finally {
      setBusy("");
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard the draft? The published wording stays as it is.")) return;
    await api.delete("/admin/beta-report/wording/draft");
    setEdits({});
    load();
  };

  const field = (key, { multiline = false } = {}) => {
    if (onlyChanged && !changedKeys.has(key)) return null;
    const isMissing = lang !== "en" && missing.has(`${lang}|${key}`) && !(edits[lang] || {})[key];
    const unsaved = (edits[lang] || {})[key] !== undefined;
    const changed = Boolean(draft.en?.[key]) || (lang === "en" && unsaved);
    const v = value(lang, key);
    return (
      <div key={key} className={`rounded-lg p-2 ${isMissing ? "bg-rose-50 ring-1 ring-rose-300" : changed ? "bg-amber-50" : ""}`} data-testid={`wording-${key}`}>
        {multiline || v.length > 70 ? (
          <textarea value={v} onChange={(e) => setText(lang, key, e.target.value)} rows={Math.min(4, Math.ceil(v.length / 90) + 1)} maxLength={600}
            className="w-full rounded-md border border-[color:var(--border-stone)] bg-white px-2 py-1.5 text-sm" />
        ) : (
          <input value={v} onChange={(e) => setText(lang, key, e.target.value)} maxLength={600}
            className="w-full rounded-md border border-[color:var(--border-stone)] bg-white px-2 py-1.5 text-sm" />
        )}
        <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-[color:var(--text-secondary)]">
          {lang !== "en" && changed && <span>English: {value("en", key)}</span>}
          {isMissing && <span className="text-rose-700">Needs a translation</span>}
          {unsaved && <span className="text-amber-700">Not saved yet</span>}
          {USES[key] > 1 && <span>Used in {USES[key]} questions; a change shows in all of them</span>}
          {lang === "en" && draft.en?.[key] && <button type="button" onClick={() => resetKey(key)} className="underline">Original</button>}
        </div>
      </div>
    );
  };

  if (!server) return <div><AdminNav title="Beta Report Wording" /><p className="p-6 text-sm">Loading…</p></div>;

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="admin-beta-report-wording">
      <AdminNav title="Beta Report Wording" />
      <div className="sticky top-0 z-10 border-b border-[color:var(--border-stone)] bg-white/95 backdrop-blur px-6 py-3">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-[color:var(--border-stone)] p-0.5" role="tablist">
            {["edit", "preview"].map((v) => (
              <button key={v} type="button" onClick={() => setView(v)} className={`rounded-full px-4 py-1.5 text-sm ${view === v ? "bg-[color:var(--brand)] text-white" : ""}`}>
                {v === "edit" ? "Edit" : "Preview as a tester"}
              </button>
            ))}
          </div>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded-lg border border-[color:var(--border-stone)] bg-white px-2 py-1.5 text-sm" data-testid="wording-language">
            {LANGS.map((l) => <option key={l} value={l}>{LANG_LABEL[l]}{l === "en" ? " (edit here)" : ""}</option>)}
          </select>
          {view === "edit" && (
            <label className="text-sm flex items-center gap-1.5">
              <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} /> Only changed
            </label>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-[color:var(--text-secondary)]" data-testid="wording-status">
              {dirty ? "Unsaved changes" : server.has_draft ? `Draft, not published${server.missing?.length ? ` · ${server.missing.length} missing` : ""}` : "Matches what testers see"}
            </span>
            {server.has_draft && <button type="button" onClick={discard} className="text-sm underline text-[color:var(--text-secondary)]">Discard draft</button>}
            <button type="button" onClick={save} disabled={!dirty || !!busy} className="rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] px-4 py-1.5 text-sm disabled:opacity-40" data-testid="wording-save">
              {busy === "save" ? "Translating…" : "Save and translate"}
            </button>
            <button type="button" onClick={publish} disabled={dirty || !server.has_draft || !!server.missing?.length || !!busy} className="rounded-full bg-[color:var(--brand)] text-white px-4 py-1.5 text-sm disabled:opacity-40" data-testid="wording-publish">
              {busy === "publish" ? "Publishing…" : "Publish"}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-6">
        {view === "edit" ? (
          <div className="space-y-6">
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              {lang === "en"
                ? "Change the English wording, then Save and translate. The Portuguese, Spanish and French versions are made for you: check them in the language list, tweak any you like, then Publish. Changed texts are highlighted. The questions themselves, their order and which are required stay as they are."
                : `Translations into ${LANG_LABEL[lang]}. Texts changed in English show the English underneath. Edit any text here to correct it, then Save.`}
              {server.published_at && <> Last published {new Date(server.published_at).toLocaleString()}{server.published_by ? ` by ${server.published_by}` : ""}.</>}
            </p>
            {GENERAL.map(([title, keys]) => (
              <section key={title} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{title}</h2>
                <div className="space-y-2">{keys.map((k) => field(k, { multiline: k === "ui.intro" }))}</div>
              </section>
            ))}
            {SECTIONS.map((section) => (
              <section key={section.key} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
                <div className="text-xs text-[color:var(--text-secondary)] mb-1">Section {section.key} · step “{value(lang, `stages.${section.stage}`)}”</div>
                {field(`sections.${section.key}`)}
                <div className="mt-3 space-y-4">
                  {section.questions.map((q) => (
                    <div key={q.id} className="border-l-2 border-[color:var(--border-stone)] pl-3">
                      <div className="text-[11px] text-[color:var(--text-secondary)]">
                        {q.required ? "Required" : "Optional"}{q.showIf ? " · only shown as a follow-up" : ""}
                      </div>
                      {q.type !== "question_number" && field(`q.${q.id}`)}
                      <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">{optionKeys(q).map((k) => field(k))}</div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <Preview lang={lang} strings={applyWording(builtInStrings(lang), { ...(draft[lang] || {}), ...(edits[lang] || {}) })} />
        )}
      </main>
    </div>
  );
}

// The form as a tester sees it, with the draft (and unsaved) wording. Nothing is saved.
function Preview({ lang, strings: s }) {
  const [stage, setStage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [mode, setMode] = useState("guided");
  const ctx = { language: lang, questionCount: 10 };
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--brand)] p-4 sm:p-6" data-testid="wording-preview">
      <p className="text-xs text-[color:var(--text-secondary)] mb-4">
        Preview only: answers aren't saved or sent. Follow-ups appear when you choose the answers that trigger them.
        {" "}Mode: {["guided", "live"].map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} className={`ml-1 underline ${mode === m ? "font-semibold" : ""}`}>{s.ui.modes[m]}</button>
        ))}
      </p>
      <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand)]">{s.ui.eyebrow}</div>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight">{s.ui.title}</h1>
      <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed">{s.ui.intro}</p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] bg-white px-4 py-1.5 text-sm">
        <span className="text-[color:var(--text-secondary)]">{s.ui.mode}:</span><strong>{s.ui.modes[mode]}</strong>
      </div>
      <ol className="mt-6 flex flex-wrap gap-2">
        {STAGES.map((st, i) => (
          <li key={st}>
            <button type="button" onClick={() => setStage(i)} className={`rounded-full px-3 py-1 text-xs border ${i === stage ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : "border-[color:var(--border-stone)] text-[color:var(--text-secondary)]"}`}>{s.stages[st]}</button>
          </li>
        ))}
      </ol>
      <div className="mt-2 text-xs text-[color:var(--text-secondary)]">{s.ui.stage(stage + 1, STAGES.length)}</div>
      {sectionsForStage(STAGES[stage]).map((section) => (
        <section key={section.key} className="mt-6 rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-5">
          <h2 className="text-lg font-semibold">{s.sections[section.key]}</h2>
          {section.questions.filter((q) => isShown(q, answers, ctx)).map((q) => (
            <Question key={q.id} q={q} s={s} value={answers[q.id]} lang={lang} questionCount={10} highlight={false}
              onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
          ))}
        </section>
      ))}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {stage > 0 && <button type="button" onClick={() => setStage(stage - 1)} className="rounded-full border border-[color:var(--border-stone)] bg-white px-5 py-2.5 text-sm">{s.ui.back}</button>}
        {stage < STAGES.length - 1
          ? <button type="button" onClick={() => setStage(stage + 1)} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-2.5 text-sm font-medium">{s.ui.next}</button>
          : <button type="button" disabled className="rounded-full bg-[color:var(--brand)] text-white px-6 py-2.5 text-sm font-medium opacity-60">{s.ui.submit}</button>}
        <span className="ml-auto text-sm text-[color:var(--text-secondary)] underline">{s.ui.later}</span>
      </div>
    </div>
  );
}
